import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { ContractProposalRejectionDialog } from "@/widgets/contract-proposal-rejection-dialog";
import { Box, Container, Stack, Typography } from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import { type DeepPartial, useForm } from "react-hook-form";
import type { AdminContractDetailPageProps } from "../../model";
import {
  type CreateOfferCargoFormSchemaType,
  createOfferCargoFormSchema,
} from "../../model/validation";
import { ContractForm } from "./forms/contract-form";
import { DeliveryProposalForm } from "./forms/delivery-proposal-form";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { ScheduleForm } from "./forms/schedule-form";
import { VehicleForm } from "./forms/vehicle-form";

export const ContractDetailInProgress: React.FC<
  AdminContractDetailPageProps
> = ({
  contractId,
  offerCargoProcess,
  defaultInputValues,
  onConfirmClick,
  onInputValuesUpdate,
  onApproveClick,
  onRejectClick,
}) => {
  // 初期化フラグ
  const [
    hasDefaultInputValuesInitialized,
    setDefaultInputValuesHasInitialized,
  ] = useState(false);
  const [hasOfferCargoInitialized, setHasOfferCargoInitialized] =
    useState(false);
  const links = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return [];
    }
    const draftOfferCargoId = offerCargoProcess.data.draftOfferCargo?.id;

    // 契約データから配送提案IDを取得
    const contractCargo = offerCargoProcess.data.contractOfferCargos?.find(
      (cargo) => cargo.id === contractId,
    );
    const deliveryProposalId = contractCargo?.sourceOfferCargoId;

    return [
      { href: "/admin/contracts", label: "契約一覧" },
      { href: `/admin/cargos/${draftOfferCargoId}`, label: "荷物詳細" },
      {
        href: `/admin/delivery-proposals/${deliveryProposalId}`,
        label: "配送提案詳細",
      },
      { label: "契約詳細" },
    ];
  }, [offerCargoProcess.status, offerCargoProcess.data, contractId]);

  // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
  const [destinationFiles, setDestinationFiles] = useState<File[]>();
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const { handleSubmit, control, watch, getValues, formState, reset, trigger } =
    useForm<CreateOfferCargoFormSchemaType>({
      resolver: customZodResolver(createOfferCargoFormSchema),
      mode: "onChange",
      defaultValues: {
        highwayFee: 1,
        destinations: [
          {
            hasLoading: true,
            hasUnloading: false,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
          {
            hasLoading: false,
            hasUnloading: true,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
        ],
      },
    });

  // offerCargoProcessのロードを待って、デフォルト値としてセットする
  useLoggingEffect(
    () => {
      // localCacheがある場合の初期化（一度だけ実行）
      if (defaultInputValues && !hasDefaultInputValuesInitialized) {
        reset(defaultInputValues);
        setDefaultInputValuesHasInitialized(true);
        return;
      }

      // 契約データが取得できた場合の初期化（一度だけ実行）
      if (
        offerCargoProcess.status === "success" &&
        offerCargoProcess.data.contractOfferCargos &&
        !hasOfferCargoInitialized &&
        !defaultInputValues
      ) {
        // contractIdに対応するcontractOfferCargoを取得
        const contractCargo = offerCargoProcess.data.contractOfferCargos.find(
          (cargo) => cargo.id === contractId,
        );

        if (contractCargo) {
          const initialValues = replaceNullWithUndefined({
            ...contractCargo,
            companyId: contractCargo.company?.id,
            destinations: contractCargo.destinations.map((dest) => ({
              ...dest,
              city: dest.address?.split(" ")[0],
              addressLine:
                dest.address?.split(" ").slice(1).join(" ") || undefined,
            })),
          });

          reset(initialValues);
          trigger();
          setHasOfferCargoInitialized(true);
        }
      }
    },
    [
      offerCargoProcess,
      reset,
      defaultInputValues,
      hasDefaultInputValuesInitialized,
      hasOfferCargoInitialized,
      contractId,
    ],
    "AdminContractDetailInProgress",
  );
  const handleInputValuesUpdate = (
    values: DeepPartial<CreateOfferCargoFormSchemaType>,
  ) => {
    onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  };
  useSubscribeFromValues(handleInputValuesUpdate, watch, true);

  const hasErrors = Object.keys(formState.errors).length > 0;

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          契約詳細
        </Typography>
      </Box>

      <Box
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <form
          onSubmit={handleSubmit((data) => {
            onConfirmClick({
              ...data,
              stage: "draft",
              destinationFiles,
            });
          })}
        >
          <Box
            display="flex"
            bgcolor="grey.50"
            minHeight="600px"
            flexDirection={"column"}
            flexGrow={1}
          >
            <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
              <Stack spacing={3} sx={{ mt: 4, mb: { xs: 4, sm: 10 }, py: 2 }}>
                <ScheduleForm control={control} />
                <DestinationsForm
                  control={control}
                  formState={formState}
                  watch={watch}
                  onDestinationFilesChange={setDestinationFiles} // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
                  onDestinationInsertOrRemove={() => {
                    // useFieldArrayのinsert/removeがwatchを発火させないため、手動で更新
                    handleInputValuesUpdate(getValues());
                  }}
                />
                <PaymentForm control={control} />
                <ContractForm control={control} />
                <VehicleForm control={control} />
                <MemoForm control={control} />
                <DeliveryProposalForm control={control} />
              </Stack>
            </Container>
          </Box>
          <Stack spacing={2} p={2} alignItems="center" justifyContent="center">
            {hasErrors && (
              <BaseMessageCard
                status="error"
                message={
                  "正しく入力されていない項目があります。再度ご確認ください。"
                }
              />
            )}
            <Stack direction="row" spacing={2}>
              <BaseButton
                variant="outlined"
                color="primary"
                sx={{ width: "180px" }}
                onClick={() => setIsRejectDialogOpen(true)}
              >
                見送る
              </BaseButton>
              <BaseButton
                variant="contained"
                color="success"
                sx={{ width: "180px" }}
                onClick={onApproveClick}
              >
                合意する
              </BaseButton>
              <BaseButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!defaultInputValues || hasErrors}
                sx={{ width: "180px" }}
              >
                確認する
              </BaseButton>
            </Stack>
          </Stack>
        </form>
      </Box>

      <ContractProposalRejectionDialog
        open={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onSubmit={(params) => {
          setIsRejectDialogOpen(false);
          onRejectClick(params);
        }}
      />
    </Box>
  );
};
